import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import otpUsersApi from 'api/requests/otpUsers';
import colors from 'theme/colors';

export default function OtpLoginPage() {
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const didAutoSubmit = useRef(false);

    useEffect(() => {
        const otpParam = searchParams.get('otp');
        if (otpParam && !didAutoSubmit.current) {
            didAutoSubmit.current = true;
            handleLogin(otpParam);
        } else {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, []);

    async function handleLogin(code) {
        const otpCode = (code || otp).trim().toUpperCase();
        if (!otpCode) return;
        setLoading(true);
        setError(null);
        try {
            const { data } = await otpUsersApi.login(otpCode);
            localStorage.setItem('otp_token', data.accessToken);
            localStorage.setItem('otp_user', JSON.stringify({
                role: data.role,
                name: data.name,
                establishments: data.establishments,
                views: data.views,
            }));
            const slug = data.establishments?.[0]?.slug;
            const view = data.views?.[0];
            if (view?.type === 'station') {
                window.location.href = `/${slug}/estacao/${view.station.slug}`;
            } else {
                window.location.href = `/${slug}/garcom`;
            }
        } catch {
            setError('Código inválido. Verifique com o responsável.');
            setLoading(false);
        }
    }

    function handleSubmit(e) {
        e.preventDefault();
        handleLogin();
    }

    // Silent auto-login — show nothing while submitting from URL param
    const isAutoLogin = searchParams.get('otp') && !error;
    if (isAutoLogin && loading) {
        return (
            <FullPage>
                <AutoLoginWrap>
                    <Spinner />
                </AutoLoginWrap>
            </FullPage>
        );
    }

    return (
        <FullPage>
            <LoginCard>
                <Brand>whizr</Brand>
                <Subtitle>Acesso do operador</Subtitle>

                <LoginForm onSubmit={handleSubmit}>
                    <OtpInput
                        ref={inputRef}
                        value={otp}
                        onChange={e => { setOtp(e.target.value.toUpperCase()); setError(null); }}
                        placeholder="Ex: JOAO-4821"
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                        $hasError={!!error}
                        disabled={loading}
                    />
                    {error && <ErrorText>{error}</ErrorText>}
                    <LoginBtn type="submit" disabled={loading || !otp.trim()}>
                        {loading ? <BtnSpinner /> : 'Entrar'}
                    </LoginBtn>
                </LoginForm>

                <HintText>Digite o código fornecido pelo responsável</HintText>
            </LoginCard>
        </FullPage>
    );
}

/* ─── Animations ─── */
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const fadeUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

/* ─── Layout ─── */
const FullPage = styled.div`
    min-height: 100dvh;
    background: ${colors.nightDark};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 20px;
    box-sizing: border-box;
`;

const AutoLoginWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LoginCard = styled.div`
    width: 100%;
    max-width: 380px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    animation: ${fadeUp} 0.35s ease both;
`;

const Brand = styled.h1`
    font-size: 32px;
    font-weight: 900;
    color: #fff;
    letter-spacing: -1px;
    margin: 0 0 6px;
`;

const Subtitle = styled.p`
    font-size: 14px;
    color: ${colors.nightLight};
    margin: 0 0 40px;
    font-weight: 500;
`;

const LoginForm = styled.form`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const OtpInput = styled.input`
    width: 100%;
    height: 64px;
    background: rgba(255,255,255,0.07);
    border: 1.5px solid ${({ $hasError }) => $hasError ? colors.danger : 'rgba(255,255,255,0.15)'};
    border-radius: 16px;
    padding: 0 20px;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 2px;
    color: #fff;
    text-align: center;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s;
    &::placeholder { color: rgba(255,255,255,0.25); font-size: 16px; letter-spacing: 0; font-weight: 400; }
    &:focus { border-color: ${({ $hasError }) => $hasError ? colors.danger : colors.occasionalPurple}; background: rgba(255,255,255,0.1); }
    &:disabled { opacity: 0.6; }
`;

const ErrorText = styled.p`
    font-size: 13px;
    color: ${colors.danger};
    margin: 0;
    text-align: center;
`;

const LoginBtn = styled.button`
    width: 100%;
    height: 56px;
    border-radius: 14px;
    background: ${({ disabled }) => disabled ? 'rgba(255,255,255,0.1)' : colors.occasionalPurple};
    color: ${({ disabled }) => disabled ? 'rgba(255,255,255,0.3)' : '#fff'};
    font-size: 17px;
    font-weight: 700;
    border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    box-shadow: ${({ disabled }) => disabled ? 'none' : `0 4px 20px ${colors.occasionalPurple}50`};
    &:not(:disabled):active { transform: scale(0.98); }
`;

const HintText = styled.p`
    font-size: 12px;
    color: rgba(255,255,255,0.3);
    margin: 20px 0 0;
    text-align: center;
`;

const Spinner = styled.div`
    width: 36px;
    height: 36px;
    border: 3px solid rgba(255,255,255,0.15);
    border-top-color: ${colors.occasionalPurple};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

const BtnSpinner = styled.div`
    width: 22px;
    height: 22px;
    border: 2.5px solid rgba(255,255,255,0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;
